import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X, Check, Send, AlertCircle, Calendar, Truck, ShoppingBag, Clock, Building2, CreditCard, User, MessageSquare, Repeat, UtensilsCrossed, AlertTriangle, FileText, MapPin, Info } from 'lucide-react';
import { Button } from './ui/Button';
export type ServiceType = 'foretagsbestallning' | 'manadens_meny' | 'catering_event';
interface OrderFormProps {
  type: ServiceType;
  onClose: () => void;
}
const serviceConfig: Record<ServiceType, {
  title: string;
  subtitle: string;
  emoji: string;
}> = {
  foretagsbestallning: {
    title: 'Företagsbeställning',
    subtitle: 'Frukostpaket, luncher & fika till kontoret',
    emoji: '🏢'
  },
  manadens_meny: {
    title: 'Månadens meny',
    subtitle: 'Upptäck vad vi erbjuder just nu',
    emoji: '📋'
  },
  catering_event: {
    title: 'Catering & Event',
    subtitle: 'Mat & fika till fest, mingel och event',
    emoji: '🥂'
  }
};
const ALLERGIES = ['Glutenfri', 'Laktosfri', 'Nötfri', 'Vegansk', 'Vegetarisk', 'Äggfri', 'Annat'];
const CATERING_CATEGORIES = [{
  id: 'tarta',
  label: '🎂 Tårta',
  desc: 'Bröllopstårta, födelsedagstårta, dessertbord m.m.'
}, {
  id: 'bakelser',
  label: '🧁 Bakelser & konditori',
  desc: 'Petit fours, bakelser, kakor och annat fint fika'
}, {
  id: 'bullar',
  label: '🍞 Bullar & bröd',
  desc: 'Nybakade bullar, frallor, mackor och bröd'
}, {
  id: 'mat',
  label: '🍽️ Mat & lunch',
  desc: 'Sallader, wraps, smörgåstårta, buffé m.m.'
}, {
  id: 'fika',
  label: '☕ Fikapaket',
  desc: 'Komplett fika med blandning av bakverk och dryck'
}];
const WEB3FORMS_KEY = 'c9228efa-9300-46fa-a21c-050430afdc38';
// Replace with your uploaded menu image/PDF URL each month
const MANADENS_MENY_URL = '';
// Replace with your uploaded package info file URLs (image or PDF)
const PACKAGE_INFO_URLS: Record<string, string> = {
  frukost: '',
  lunch: '',
  fika: '' // URL to fikapaket info/prices
};
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const textareaClass = 'w-full min-h-[100px] p-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans resize-y text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const req = <span className="text-red-500 ml-0.5" data-id="element-862" data-mp="2icron9nyhcff">*</span>;
const checkboxClass = 'flex items-center gap-2.5 p-2.5 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]';
const SectionHeading = ({
  icon: Icon,
  title
}: {
  icon: React.ElementType;
  title: string;
}) => <h4 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2 border-l-4 border-[#C5A55A] pl-3" data-id="element-863" data-mp="1zl8mf51ra2eg">
    <Icon className="w-5 h-5 text-[#C5A55A]" data-id="element-864" data-mp="1pfh4cgahblln" />
    {title}
  </h4>;
// Google Places Autocomplete hook
function useGooglePlaces(inputRef: React.RefObject<HTMLInputElement | null>, onSelect: (address: string) => void) {
  useEffect(() => {
    if (!inputRef.current) return;
    // Check if Google Maps is loaded
    if (typeof window !== 'undefined' && (window as any).google?.maps?.places) {
      const autocomplete = new (window as any).google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: {
          country: 'se'
        }
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address) {
          onSelect(place.formatted_address);
        }
      });
    }
  }, [inputRef, onSelect]);
}
export function OrderForm({
  type,
  onClose
}: OrderFormProps) {
  const config = serviceConfig[type];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<Record<string, string>>({
    orgNummer: '',
    foretagsnamn: '',
    kontaktperson: '',
    email: '',
    phone: '',
    faktureringsEmail: '',
    bokningsReferens: '',
    orderType: '',
    paymentMethod: '',
    fakturaForetagsnamn: '',
    fakturaOrgNr: '',
    fakturaEmail: '',
    fakturaAdress: '',
    fakturaReferens: '',
    frequency: '',
    frequencyDays: '',
    deliveryType: 'Avhämtning',
    deliveryAddress: '',
    date: '',
    time: '',
    frukostAntal: '',
    frukostVariant: '',
    lunchAntal: '',
    lunchVariant: '',
    fikaAntal: '',
    fikaVariant: '',
    fikaPreferenser: '',
    fikaWantsDrink: '',
    fikaDrinkType: '',
    allergiAnnat: '',
    antalPersoner: '',
    eventType: '',
    cateringProjectMgmt: '',
    cateringProjectMgmtDetails: '',
    cateringHireStaff: '',
    cateringHireStaffDetails: '',
    cateringDrink: '',
    cateringDrinkDetails: '',
    details: ''
  });
  // Allergy state: which are selected + count per allergy
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);
  const [allergyCounts, setAllergyCounts] = useState<Record<string, string>>({});
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryNotes, setCategoryNotes] = useState<Record<string, string>>({});
  const [activePackageInfo, setActivePackageInfo] = useState<string | null>(null);
  const isFöretag = type === 'foretagsbestallning';
  const isCatering = type === 'catering_event';
  const isMeny = type === 'manadens_meny';
  const isStående = formData.orderType === 'Stående bokning';
  const isEngångs = formData.orderType === 'Engångsbeställning';
  const isFaktura = formData.paymentMethod === 'Faktura';
  const isUtkörning = formData.deliveryType === 'Utkörning';
  const handleAddressSelect = useCallback((address: string) => {
    setFormData(prev => ({
      ...prev,
      deliveryAddress: address
    }));
  }, []);
  useGooglePlaces(addressInputRef, handleAddressSelect);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const toggleAllergy = (allergy: string) => {
    setSelectedAllergies(prev => {
      if (prev.includes(allergy)) {
        // Remove allergy and its count
        const newCounts = {
          ...allergyCounts
        };
        delete newCounts[allergy];
        setAllergyCounts(newCounts);
        return prev.filter(a => a !== allergy);
      }
      return [...prev, allergy];
    });
  };
  const setAllergyCount = (allergy: string, count: string) => {
    setAllergyCounts(prev => ({
      ...prev,
      [allergy]: count
    }));
  };
  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(cat)) {
        const newNotes = {
          ...categoryNotes
        };
        delete newNotes[cat];
        setCategoryNotes(newNotes);
        return prev.filter(c => c !== cat);
      }
      return [...prev, cat];
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_KEY,
      subject: `Ny förfrågan: ${config.title} – ${isFöretag ? formData.foretagsnamn : formData.kontaktperson}`,
      from_name: 'Top Notch Bakery Beställningar',
      replyto: formData.email,
      Typ: config.title
    };
    if (isFöretag) {
      payload['Organisationsnummer'] = formData.orgNummer;
      payload['Företagsnamn'] = formData.foretagsnamn;
      payload['Faktureringsmejl'] = formData.faktureringsEmail;
      payload['Referens för bokning'] = formData.bokningsReferens;
    }
    payload['Kontaktperson'] = formData.kontaktperson;
    payload['E-post'] = formData.email;
    payload['Telefon'] = formData.phone;
    if (!isMeny) {
      payload['Ordertyp'] = formData.orderType;
      if (isEngångs) {
        payload['Betalning'] = formData.paymentMethod;
        if (isFaktura && !isFöretag) {
          payload['Faktura – Företagsnamn'] = formData.fakturaForetagsnamn;
          payload['Faktura – Org.nr'] = formData.fakturaOrgNr;
          payload['Faktura – E-post'] = formData.fakturaEmail;
          payload['Faktura – Adress'] = formData.fakturaAdress;
          payload['Faktura – Referens'] = formData.fakturaReferens || '(Ej angivet)';
        }
      }
      if (isStående) {
        payload['Frekvens'] = formData.frequency;
        if (formData.frequencyDays) payload['Specifika dagar'] = formData.frequencyDays;
      }
      payload['Leveranssätt'] = formData.deliveryType;
      if (isUtkörning && formData.deliveryAddress) payload['Leveransadress'] = formData.deliveryAddress;
      payload['Önskat datum'] = formData.date;
      payload['Önskad tid'] = formData.time || '(Ej angivet)';
    }
    if (isFöretag) {
      if (formData.frukostAntal) {
        payload['Frukostpaket – antal'] = formData.frukostAntal;
        if (formData.frukostVariant) payload['Frukostpaket – variant'] = `Alternativ ${formData.frukostVariant}`;
      }
      if (formData.lunchAntal) {
        payload['Lunchpaket – antal'] = formData.lunchAntal;
        if (formData.lunchVariant) payload['Lunchpaket – variant'] = `Alternativ ${formData.lunchVariant}`;
      }
      if (formData.fikaAntal) {
        payload['Fikapaket – antal'] = formData.fikaAntal;
        if (formData.fikaVariant) payload['Fikapaket – variant'] = formData.fikaVariant;
        if (formData.fikaPreferenser) payload['Fikapaket – preferenser'] = formData.fikaPreferenser;
        if (formData.fikaWantsDrink === 'Ja') {
          payload['Fikapaket – dryck'] = 'Ja';
          payload['Fikapaket – drycktyp'] = formData.fikaDrinkType || '(Ej angivet)';
        } else {
          payload['Fikapaket – dryck'] = 'Nej';
        }
      }
    }
    if (isCatering) {
      payload['Valda kategorier'] = selectedCategories.length > 0 ? selectedCategories.join(', ') : '(Inga valda)';
      // Per-category preferences
      selectedCategories.forEach(catId => {
        const cat = CATERING_CATEGORIES.find(c => c.id === catId);
        if (cat && categoryNotes[catId]) {
          payload[`${cat.label} – preferenser`] = categoryNotes[catId];
        }
      });
      payload['Typ av event'] = formData.eventType || '(Ej angivet)';
      payload['Antal personer'] = formData.antalPersoner;
      if (formData.cateringDrink === 'Ja') {
        payload['Dryck till eventet'] = 'Ja';
        payload['Dryck – detaljer'] = formData.cateringDrinkDetails || '(Ej angivet)';
      } else {
        payload['Dryck till eventet'] = formData.cateringDrink || '(Ej angivet)';
      }
      if (formData.cateringProjectMgmt === 'Ja') {
        payload['Intresserad av projektledning'] = 'Ja';
        payload['Projektledning – detaljer'] = formData.cateringProjectMgmtDetails || '(Ej angivet)';
      } else {
        payload['Intresserad av projektledning'] = formData.cateringProjectMgmt || 'Nej';
      }
      if (formData.cateringHireStaff === 'Ja') {
        payload['Intresserad av inhyrd personal'] = 'Ja';
        payload['Inhyrd personal – detaljer'] = formData.cateringHireStaffDetails || '(Ej angivet)';
      } else {
        payload['Intresserad av inhyrd personal'] = formData.cateringHireStaff || 'Nej';
      }
    }
    // Allergies with counts
    const allergyDetails = selectedAllergies.filter(a => a !== 'Annat').map(a => {
      const count = allergyCounts[a];
      return count ? `${a} (${count} pers)` : a;
    });
    if (selectedAllergies.includes('Annat')) {
      const annatCount = allergyCounts['Annat'];
      const annatText = formData.allergiAnnat || 'Ej specificerat';
      allergyDetails.push(`Annat: ${annatText}${annatCount ? ` (${annatCount} pers)` : ''}`);
    }
    payload['Allergier'] = allergyDetails.length > 0 ? allergyDetails.join(', ') : '(Inga)';
    payload['Beställningsdetaljer / Meddelande'] = formData.details;
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss på order@topnotchbakery.se');
      }
    } catch {
      setSubmitError('Kunde inte skicka. Kontrollera din anslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-865" data-mp="7zjsruzkd9io">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-866" data-mp="hw6f6o7jg8mt">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-867" data-mp="1xc5i9l2ateg7">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-868" data-mp="1taa7zr2e3qqa" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-2" data-id="element-869" data-mp="2olrx04supao1">
            Tack för din förfrågan!
          </h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-870" data-mp="2smbsz51ou88l">
            Vi har tagit emot din offertförfrågan gällande{' '}
            <span className="font-semibold text-[#1A1A1A]" data-id="element-871" data-mp="2xklpbiz5fevs">{config.title}</span>
            . Vi återkommer till dig med en offert inom 24 timmar.
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-872" data-mp="37kvy8re5i055">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-873" data-mp="3bk4vw7cbai5z">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-874" data-mp="3ldlmz3l2dj5y">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-875" data-mp="3vagiddm1j3g0">
          <div className="flex items-start gap-4" data-id="element-876" data-mp="1jk8rmtczsss9">
            <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-877" data-mp="1emgxdsovkukw">
              {config.emoji}
            </div>
            <div data-id="element-878" data-mp="14q49xcjxbcyl">
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-879" data-mp="14qmagldmcdcb">
                {config.title}
              </h3>
              <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-880" data-mp="22cbb0ch0qn67">
                {config.subtitle}
              </p>
              <p className="text-xs text-[#1A1A1A]/50" data-id="element-881" data-mp="1smrrr5pe8rat">
                Skickas till order@topnotchbakery.se
              </p>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#C5A55A]/10 text-[#C5A55A] text-xs font-medium rounded-md" data-id="element-882" data-mp="1nn5bene9quxs">
                <Clock className="w-3.5 h-3.5" data-id="element-883" data-mp="2l5f9qc93fq7o" />
                Svar inom 24 timmar
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-884" data-mp="ef241ex53ax2">
            <X className="w-6 h-6" data-id="element-885" data-mp="4oh8y1gocoiw" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-886" data-mp="9nttugnzjliu">
          <form id="order-form" onSubmit={handleSubmit} className="space-y-8" data-id="element-887" data-mp="9m92t10vqkpq">
            {/* === MÅNADENS MENY: Display menu === */}
            {isMeny && <div className="space-y-4" data-id="element-888" data-mp="3vxd9ifyift4t">
                <SectionHeading icon={FileText} title="Månadens meny" data-id="element-889" data-mp="3hw0uq7v35xki" />
                {MANADENS_MENY_URL ? <div className="rounded-xl border border-[#C5A55A]/20 overflow-hidden bg-white" data-id="element-890" data-mp="3mults3ylqb7d">
                    {MANADENS_MENY_URL.endsWith('.pdf') ? <iframe src={MANADENS_MENY_URL} className="w-full h-[500px]" title="Månadens meny" data-id="element-891" data-mp="1g2l0z23pc2lb" /> : <img src={MANADENS_MENY_URL} alt="Månadens meny" className="w-full h-auto" data-id="element-892" data-mp="30myji5c8oy4e" />}
                  </div> : <div className="p-8 rounded-xl border-2 border-dashed border-[#C5A55A]/30 bg-[#FFF8F0]/50 text-center" data-id="element-893" data-mp="3ajl36qstxj4u">
                    <FileText className="w-10 h-10 text-[#C5A55A]/40 mx-auto mb-3" data-id="element-894" data-mp="3oe40woxtvygt" />
                    <p className="text-sm text-[#1A1A1A]/50 font-medium" data-id="element-895" data-mp="3jfakann8vrcf">
                      Menyn uppdateras varje månad
                    </p>
                    <p className="text-xs text-[#1A1A1A]/40 mt-1" data-id="element-896" data-mp="3tb450ub4ar5a">
                      Kontakta oss nedan för att få den senaste menyn
                    </p>
                  </div>}
              </div>}

            {/* === FÖRETAGSUPPGIFTER === */}
            {isFöretag && <div className="space-y-4" data-id="element-897" data-mp="1d35o8ijv24cu">
                <SectionHeading icon={Building2} title="Företagsuppgifter" data-id="element-898" data-mp="1d22wy9imlzwe" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-899" data-mp="1i1pciplxfs8d">
                  <div data-id="element-900" data-mp="1mtqm23rpn4up">
                    <label className={labelClass} data-id="element-901" data-mp="1muie54m3mkg8">Företagsnamn {req}</label>
                    <input type="text" name="foretagsnamn" required value={formData.foretagsnamn} onChange={handleChange} className={inputClass} placeholder="Företagets namn" data-id="element-902" data-mp="1rs1se4isqx86" />
                  </div>
                  <div data-id="element-903" data-mp="184ufyc97s1wq">
                    <label className={labelClass} data-id="element-904" data-mp="1865g823garj3">
                      Organisationsnummer {req}
                    </label>
                    <input type="text" name="orgNummer" required value={formData.orgNummer} onChange={handleChange} className={inputClass} placeholder="XXXXXX-XXXX" data-id="element-905" data-mp="2fie9o5zxm6bf" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-906" data-mp="3v8ovmdwqrhk8">
                  <div data-id="element-907" data-mp="3qbori8jgcovr">
                    <label className={labelClass} data-id="element-908" data-mp="3qa3xck11v7yf">Faktureringsmejl {req}</label>
                    <input type="email" name="faktureringsEmail" required value={formData.faktureringsEmail} onChange={handleChange} className={inputClass} placeholder="faktura@foretag.se" data-id="element-909" data-mp="3lc2cbtk2cykb" />
                  </div>
                  <div data-id="element-910" data-mp="1akamw7cyhflt">
                    <label className={labelClass} data-id="element-911" data-mp="1aig1yt1opnhq">
                      Referens för bokning {req}
                    </label>
                    <input type="text" name="bokningsReferens" required value={formData.bokningsReferens} onChange={handleChange} className={inputClass} placeholder="T.ex. avdelning, kostnadsställe" data-id="element-912" data-mp="15ilaoodzyvw5" />
                  </div>
                </div>
              </div>}

            {/* === KONTAKTUPPGIFTER === */}
            <div className="space-y-4" data-id="element-913" data-mp="1f8kvlqp1s9d5">
              <SectionHeading icon={User} title="Kontaktuppgifter" data-id="element-914" data-mp="1b55418ru4z7b" />
              <div data-id="element-915" data-mp="1b811kvzrtlv8">
                <label className={labelClass} data-id="element-916" data-mp="166bo79p2sw3z">Kontaktperson {req}</label>
                <input type="text" name="kontaktperson" required value={formData.kontaktperson} onChange={handleChange} className={inputClass} placeholder="För- och efternamn" data-id="element-917" data-mp="168o5rlqx94gi" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-918" data-mp="335kex7bctqjr">
                <div data-id="element-919" data-mp="3336h4qpai08i">
                  <label className={labelClass} data-id="element-920" data-mp="383luvjdhh9r0">E-post {req}</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="din@epost.se" data-id="element-921" data-mp="381r8s1qh7blc" />
                </div>
                <div data-id="element-922" data-mp="2p4gbgmoems8j">
                  <label className={labelClass} data-id="element-923" data-mp="2p617yhua4l91">Telefon {req}</label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} placeholder="070-123 45 67" data-id="element-924" data-mp="2u3t81z52vdob" />
                </div>
              </div>
            </div>

            {/* === ORDERTYP (not for månadens meny) === */}
            {!isMeny && <div className="space-y-4" data-id="element-925" data-mp="2aifqgva2gx9o">
                <SectionHeading icon={Repeat} title="Ordertyp" data-id="element-926" data-mp="25i1kdis0zxea" />
                <div className="space-y-3" data-id="element-927" data-mp="25jmhng5q5ufu">
                  <label className={labelClass} data-id="element-928" data-mp="2jfzxylyivpsq">Typ av order {req}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-929" data-mp="2fewhbx8nhpqg">
                    {['Engångsbeställning', 'Stående bokning'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-930" data-mp="1rq3kzzo49fvb">
                        <input type="radio" name="orderType" value={option} required checked={formData.orderType === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-931" data-mp="1vpccexvop1np" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-932" data-mp="jyeobolkzdvu">{option}</span>
                      </label>)}
                  </div>
                </div>

                {/* Engångs: Payment */}
                {isEngångs && <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-933" data-mp="1cnd6glppeg15">
                    <label className={labelClass} data-id="element-934" data-mp="12owafabwrb88">
                      <CreditCard className="w-4 h-4 inline mr-1.5 text-[#C5A55A]" data-id="element-935" data-mp="12oe7xvynk0rv" />
                      Betalningssätt {req}
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-936" data-mp="3nedcdvghnrpq">
                      {['Betala på plats', 'Faktura'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-937" data-mp="3sf9f45p3t7tk">
                          <input type="radio" name="paymentMethod" value={option} required checked={formData.paymentMethod === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-938" data-mp="2nsvz15yxfj8" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-939" data-mp="gga4bbhjvb43">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {isFaktura && !isFöretag && <div className="space-y-4 pt-2 border-t border-[#C5A55A]/10 mt-3 animate-in slide-in-from-top-1 duration-150" data-id="element-940" data-mp="1gpryl4bz88bf">
                        <p className="text-xs text-[#1A1A1A]/50 pt-2" data-id="element-941" data-mp="1lpmp36ruavvy">
                          Fyll i uppgifter för fakturering:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-942" data-mp="1veedj4xoyy24">
                          <div data-id="element-943" data-mp="1vgh9s6pftnfa">
                            <label className={labelClass} data-id="element-944" data-mp="20e0pzc5p4a9v">
                              Företagsnamn / Namn {req}
                            </label>
                            <input type="text" name="fakturaForetagsnamn" required value={formData.fakturaForetagsnamn} onChange={handleChange} className={inputClass} placeholder="Fakturamottagare" data-id="element-945" data-mp="24d9lalgdwg7j" />
                          </div>
                          <div data-id="element-946" data-mp="bzl05rem0m28">
                            <label className={labelClass} data-id="element-947" data-mp="bytf3ain26q7">
                              Org.nr / Personnr {req}
                            </label>
                            <input type="text" name="fakturaOrgNr" required value={formData.fakturaOrgNr} onChange={handleChange} className={inputClass} placeholder="XXXXXX-XXXX" data-id="element-948" data-mp="lx90cwuzilrs" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-949" data-mp="zo4dwmgfhr1p">
                          <div data-id="element-950" data-mp="zqgp8grol35s">
                            <label className={labelClass} data-id="element-951" data-mp="1ekvdowu0l4ru">
                              Fakturamejl {req}
                            </label>
                            <input type="email" name="fakturaEmail" required value={formData.fakturaEmail} onChange={handleChange} className={inputClass} placeholder="faktura@epost.se" data-id="element-952" data-mp="19nv3c9q4tfux" />
                          </div>
                          <div data-id="element-953" data-mp="19jkxd9euhkly">
                            <label className={labelClass} data-id="element-954" data-mp="19ijmb4xtwy48">
                              Fakturaadress {req}
                            </label>
                            <input type="text" name="fakturaAdress" required value={formData.fakturaAdress} onChange={handleChange} className={inputClass} placeholder="Gatuadress, postnr, ort" data-id="element-955" data-mp="10jeqw7hkdkwa" />
                          </div>
                        </div>
                        <div data-id="element-956" data-mp="170rlx2eod8d6">
                          <label className={labelClass} data-id="element-957" data-mp="170htl865qp4b">Referens</label>
                          <input type="text" name="fakturaReferens" value={formData.fakturaReferens} onChange={handleChange} className={inputClass} placeholder="T.ex. kostnadsställe, avdelning" data-id="element-958" data-mp="121odd7zhkk0g" />
                        </div>
                      </div>}
                  </div>}

                {/* Stående: Date + Frequency */}
                {isStående && <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4 animate-in slide-in-from-top-2 duration-200" data-id="element-959" data-mp="ea9bthfja6q9">
                    <label className={labelClass} data-id="element-960" data-mp="4exviuoh8rge">
                      <Repeat className="w-4 h-4 inline mr-1.5 text-[#C5A55A]" data-id="element-961" data-mp="3qfdmece13mzr" />
                      Återkommande frekvens {req}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-962" data-mp="3vcvl79yc0lu1">
                      {['Varje vecka', 'Varje månad', 'Specifika dagar'].map(option => <label key={option} className="flex items-center gap-2 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A]" data-id="element-963" data-mp="95x9c57o148g">
                            <input type="radio" name="frequency" value={option} required checked={formData.frequency === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-964" data-mp="3hlqskrtdstt7" />
                            <span className="text-xs text-[#1A1A1A]" data-id="element-965" data-mp="276ej3drh7vly">
                              {option}
                            </span>
                          </label>)}
                    </div>

                    {(formData.frequency === 'Varje vecka' || formData.frequency === 'Varje månad') && <p className="text-xs text-[#1A1A1A]/50 bg-white/60 p-3 rounded-lg border border-[#C5A55A]/10 animate-in slide-in-from-top-1 duration-150" data-id="element-966" data-mp="1o70r31db4uy3">
                        📅 Första leveransen bestäms nedan under{' '}
                        <span className="font-semibold" data-id="element-967" data-mp="1ei90awjzggor">Leverans & tid</span>{' '}
                        och återkommer sedan{' '}
                        {formData.frequency === 'Varje vecka' ? 'samma dag varje vecka' : 'samma datum varje månad'}
                        .
                      </p>}

                    {formData.frequency === 'Specifika dagar' && <div className="space-y-3 animate-in slide-in-from-top-1 duration-150" data-id="element-968" data-mp="gy951k3b5s7o">
                        <label className={labelClass} data-id="element-969" data-mp="utb7tycuz1g9">
                          Vilka dagar vill ni ha leverans? {req}
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-970" data-mp="14str0cky2j79">
                          {['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'].map(day => <label key={day} className={checkboxClass} data-id="element-971" data-mp="1njjspxys3zyj">
                              <input type="checkbox" checked={(formData.frequencyDays || '').includes(day)} onChange={() => {
                      const current = formData.frequencyDays ? formData.frequencyDays.split(', ').filter(Boolean) : [];
                      const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
                      setFormData(prev => ({
                        ...prev,
                        frequencyDays: updated.join(', ')
                      }));
                    }} className="w-4 h-4 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-972" data-mp="1il9ybpkvp3dz" />
                              <span className="text-sm text-[#1A1A1A]" data-id="element-973" data-mp="3779nbape6g9s">
                                {day}
                              </span>
                            </label>)}
                        </div>
                      </div>}
                  </div>}
              </div>}

            {/* === LEVERANS & TID (not for månadens meny) === */}
            {!isMeny && <div className="space-y-4" data-id="element-974" data-mp="3l57xs50ldsk0">
                <SectionHeading icon={Truck} title="Leverans & tid" data-id="element-975" data-mp="3l6j3iadlladr" />

                {isStående && <div className="p-3 bg-[#C5A55A]/5 rounded-lg border border-[#C5A55A]/15 animate-in fade-in duration-200" data-id="element-976" data-mp="3g4tu0z60x4rs">
                    <p className="text-xs text-[#1A1A1A]/60 leading-relaxed" data-id="element-977" data-mp="24rhwf60uxie2">
                      ℹ️ Vald tid för avhämtning eller leverans gäller under
                      hela den återkommande frekvensen ni valt ovan.
                    </p>
                  </div>}

                <div className="space-y-3" data-id="element-978" data-mp="3okrctk912z9s">
                  <label className={labelClass} data-id="element-979" data-mp="3tecfvqphajfu">Leveranssätt {req}</label>
                  <div className="flex gap-4" data-id="element-980" data-mp="3eooeyql7lmsn">
                    {['Avhämtning', 'Utkörning'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A] flex-1" data-id="element-981" data-mp="3jmpy1bimxm44">
                        <input type="radio" name="deliveryType" value={option} checked={formData.deliveryType === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-982" data-mp="25yby9y7vv2d" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-983" data-mp="2je9m887k0doj">{option}</span>
                      </label>)}
                  </div>
                </div>
                {isUtkörning && <div className="space-y-3 animate-in slide-in-from-top-1 duration-150" data-id="element-984" data-mp="2tcywpfx4evur">
                    {isStående && <p className="text-xs text-[#1A1A1A]/50 bg-white/60 p-3 rounded-lg border border-[#C5A55A]/10" data-id="element-985" data-mp="2ocjlatwefyff">
                        🚚 Utkörning för stående bokningar baseras på vald
                        frekvens och ordervärde på beställningen.
                      </p>}
                    <label className={labelClass} data-id="element-986" data-mp="2agfxpjg16obx">
                      <MapPin className="w-4 h-4 inline mr-1 text-[#C5A55A]" data-id="element-987" data-mp="25n4l11ojfdc3" />
                      Leveransadress {req}
                    </label>
                    <p className="text-xs text-[#1A1A1A]/40 mb-1.5" data-id="element-988" data-mp="1cz8r24qcrzj0">
                      Börja skriva för att söka adress
                    </p>
                    <input ref={addressInputRef} type="text" name="deliveryAddress" required value={formData.deliveryAddress} onChange={handleChange} className={inputClass} placeholder="Sök gatuadress, postnummer, ort..." autoComplete="off" data-id="element-989" data-mp="22mbe4pvit5er" />
                  </div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-990" data-mp="1nqviigfal1g9">
                  <div data-id="element-991" data-mp="1sonk625qo1xn">
                    <label className={labelClass} data-id="element-992" data-mp="1sof7aa9m8wmg">
                      {isStående ? 'Startdatum (första leveransen)' : 'Önskat datum'}{' '}
                      {req}
                    </label>
                    <div className="relative" data-id="element-993" data-mp="346q4xa524r9p">
                      <input type="date" name="date" required value={formData.date} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-994" data-mp="392dsxm7szedm" />
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-995" data-mp="noz1a9gsvk82" />
                    </div>
                  </div>
                  <div data-id="element-996" data-mp="eqn4tqt95s7p">
                    <label className={labelClass} data-id="element-997" data-mp="er6n58mteim9">
                      Önskad tid{' '}
                      {isStående && <span className="text-xs font-normal text-[#1A1A1A]/40 ml-1" data-id="element-998" data-mp="9svcwwsly63i">
                          (gäller alla leveranser)
                        </span>}
                    </label>
                    <div className="relative" data-id="element-999" data-mp="1z56xlt5n1zba">
                      <input type="time" name="time" value={formData.time} onChange={handleChange} className={`${inputClass} pl-10`} data-id="element-1000" data-mp="1z4x806oi9u64" />
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-1001" data-mp="17z1kkndmm4vk" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* === FÖRETAG: PAKETVAL === */}
            {isFöretag && <div className="space-y-5" data-id="element-1002" data-mp="k9qtxypzpqv5">
                <SectionHeading icon={ShoppingBag} title="Välj paket" data-id="element-1003" data-mp="kcv4ddu1tiu9" />
                <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-1004" data-mp="pb6bhgsylf8s">
                  Ange antal av varje paket och välj alternativ (1, 2 eller 3).
                  Tryck på info-knappen för att se utbud och priser.
                </p>
                <div className="space-y-4" data-id="element-1005" data-mp="25nv8eu66o15z">
                  {[{
                key: 'frukost',
                antalKey: 'frukostAntal',
                variantKey: 'frukostVariant',
                label: '🥐 Frukostpaket'
              }, {
                key: 'lunch',
                antalKey: 'lunchAntal',
                variantKey: 'lunchVariant',
                label: '🥗 Lunchpaket'
              }].map(pkg => <div key={pkg.key} className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-1006" data-mp="2zmwqwolakij">
                      <div className="flex items-center justify-between" data-id="element-1007" data-mp="cpy3eqznr7t4">
                        <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1008" data-mp="qraf2q7scn97">
                          {pkg.label}
                        </span>
                        <button type="button" onClick={() => setActivePackageInfo(pkg.key)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C5A55A] bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 rounded-lg transition-colors" data-id="element-1009" data-mp="lsh0svkocs7z">
                          <Info className="w-3.5 h-3.5" data-id="element-1010" data-mp="yr1ivxb4ltjh" />
                          Se utbud & priser
                        </button>
                      </div>

                      <div className="flex items-center gap-4" data-id="element-1011" data-mp="1cnwqrmfn5exy">
                        <label className="text-xs text-[#1A1A1A]/60 shrink-0" data-id="element-1012" data-mp="17qenocob1txv">
                          Antal:
                        </label>
                        <input type="number" name={pkg.antalKey} min="0" value={formData[pkg.antalKey]} onChange={handleChange} className={`${inputClass} max-w-[100px]`} placeholder="0" data-id="element-1013" data-mp="1hpdqvfqawxdh" />
                        <span className="text-xs text-[#1A1A1A]/40" data-id="element-1014" data-mp="25clxq23doehm">st</span>
                      </div>

                      <div data-id="element-1015" data-mp="2k1niapyopi5a">
                        <label className="text-xs text-[#1A1A1A]/60 block mb-2" data-id="element-1016" data-mp="2k1dtv6tafj1p">
                          Välj alternativ:
                        </label>
                        <div className="flex gap-3" data-id="element-1017" data-mp="2oydz5fibchrr">
                          {['1', '2', '3'].map(variant => <label key={variant} className="flex items-center justify-center w-12 h-10 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#C5A55A]/15 has-[:checked]:border-[#C5A55A] has-[:checked]:font-semibold" data-id="element-1018" data-mp="2f2alpdi0gwpi">
                              <input type="radio" name={pkg.variantKey} value={variant} checked={formData[pkg.variantKey] === variant} onChange={handleChange} className="sr-only" data-id="element-1019" data-mp="1ravi7hei09co" />
                              <span className="text-sm text-[#1A1A1A]" data-id="element-1020" data-mp="3p2ddeqgdkse6">
                                {variant}
                              </span>
                            </label>)}
                        </div>
                      </div>
                    </div>)}

                  {/* ☕ Fikapaket – custom descriptive version */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-4" data-id="element-1021" data-mp="bt8vwr3wxud4">
                    <div className="flex items-center justify-between" data-id="element-1022" data-mp="6sunh7yegiek">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1023" data-mp="1ujc2ssghzu8">
                        ☕ Fikapaket
                      </span>
                      <button type="button" onClick={() => setActivePackageInfo('fika')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C5A55A] bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 rounded-lg transition-colors" data-id="element-1024" data-mp="3u1zsz6joekq5">
                        <Info className="w-3.5 h-3.5" data-id="element-1025" data-mp="1252k4xva9xmz" />
                        Se utbud & priser
                      </button>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-1026" data-mp="171ay47xqvksk">
                      <label className="text-xs text-[#1A1A1A]/60 shrink-0" data-id="element-1027" data-mp="1bvf3wnv9xms2">
                        Antal:
                      </label>
                      <input type="number" name="fikaAntal" min="0" value={formData.fikaAntal} onChange={handleChange} className={`${inputClass} max-w-[100px]`} placeholder="0" data-id="element-1028" data-mp="1ls0a3cj153xh" />
                      <span className="text-xs text-[#1A1A1A]/40" data-id="element-1029" data-mp="3rmt84nxuyu1h">st</span>
                    </div>

                    <div data-id="element-1030" data-mp="exgfd9nmuhk">
                      <label className="text-xs text-[#1A1A1A]/60 block mb-2" data-id="element-1031" data-mp="319wlaf67m3ut">
                        Välj typ av fika:
                      </label>
                      <div className="space-y-2" data-id="element-1032" data-mp="2wa0hafxiicf9">
                        {[{
                      value: 'Bullar & kakor',
                      label: 'Bullar & kakor',
                      desc: 'Bullar, kakor, chokladbollar, chokladdoppade bars m.m. (varierar efter dagens utbud)'
                    }, {
                      value: 'Kondisbakelser',
                      label: 'Kondisbakelser',
                      desc: 'Fina bakelser från vårt konditori'
                    }, {
                      value: 'Tårta & morotskaka',
                      label: 'Tårta & morotskaka',
                      desc: 'Morotskaka, tårta och andra större bakverk'
                    }].map(option => <label key={option.value} className="flex items-start gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-1033" data-mp="aaqr3j73mxqd">
                            <input type="radio" name="fikaVariant" value={option.value} checked={formData.fikaVariant === option.value} onChange={handleChange} className="w-4 h-4 mt-0.5 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1034" data-mp="5cyptykkdz9i" />
                            <div data-id="element-1035" data-mp="34ratr50p5r6m">
                              <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1036" data-mp="34q9703abjg92">
                                {option.label}
                              </span>
                              <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-1037" data-mp="1fayhrzrz4ng9">
                                {option.desc}
                              </p>
                            </div>
                          </label>)}
                      </div>
                    </div>

                    {/* Preferences textarea */}
                    <div data-id="element-1038" data-mp="10jpvo0na5k7v">
                      <label className={labelClass} data-id="element-1039" data-mp="wjxlxkm5h80x">
                        Preferenser eller önskemål
                      </label>
                      <textarea name="fikaPreferenser" value={formData.fikaPreferenser} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '70px'
                  }} placeholder="T.ex. 'Gärna extra chokladbollar', 'Inga nötter', 'Blandning av allt'..." data-id="element-1040" data-mp="rk2wzmlxqsii" />
                    </div>

                    {/* Drink option */}
                    <div className="border-t border-[#C5A55A]/10 pt-4 space-y-3" data-id="element-1041" data-mp="2kg5fw85gdnpp">
                      <label className={labelClass} data-id="element-1042" data-mp="2ohqpnkmoxlvo">
                        Vill ni ha dryck till fikan?
                      </label>
                      <div className="flex gap-3" data-id="element-1043" data-mp="2y93gqa0o080n">
                        {['Ja', 'Nej'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A] flex-1" data-id="element-1044" data-mp="21jm1b3bvr7lr">
                            <input type="radio" name="fikaWantsDrink" value={option} checked={formData.fikaWantsDrink === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1045" data-mp="2fjoguiwptoxu" />
                            <span className="text-sm text-[#1A1A1A]" data-id="element-1046" data-mp="bt0ladrz8hbf">
                              {option}
                            </span>
                          </label>)}
                      </div>

                      {formData.fikaWantsDrink === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-1047" data-mp="zmsej97hojbv">
                          <label className={labelClass} data-id="element-1048" data-mp="ulbh5no7rf01">
                            Vilken typ av dryck önskas?
                          </label>
                          <textarea name="fikaDrinkType" value={formData.fikaDrinkType} onChange={handleChange} className={textareaClass} style={{
                      minHeight: '60px'
                    }} placeholder="T.ex. 'Kaffe och te', 'Juice och vatten', 'Blandning av kaffe, te och juice'..." data-id="element-1049" data-mp="13lrhio9nu60o" />
                        </div>}
                    </div>
                  </div>
                </div>
              </div>}

            {/* Package Info Popup */}
            {activePackageInfo && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150" onClick={e => {
            if (e.target === e.currentTarget) setActivePackageInfo(null);
          }} data-id="element-1050" data-mp="123j5cjlzpatn">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200" data-id="element-1051" data-mp="x83rhrnzfex8">
                  <div className="flex items-center justify-between p-5 border-b border-[#C5A55A]/10 shrink-0" data-id="element-1052" data-mp="2rkbrvkj2oo0e">
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-1053" data-mp="3ua8v44n9rc40">
                      {activePackageInfo === 'frukost' && '🥐 Frukostpaket'}
                      {activePackageInfo === 'lunch' && '🥗 Lunchpaket'}
                      {activePackageInfo === 'fika' && '☕ Fikapaket'}
                      {' – Utbud & priser'}
                    </h4>
                    <button type="button" onClick={() => setActivePackageInfo(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-1054" data-mp="gu9uqvswtiff">
                      <X className="w-5 h-5" data-id="element-1055" data-mp="uuklzzy4sz14" />
                    </button>
                  </div>
                  <div className="overflow-y-auto flex-1 p-5" data-id="element-1056" data-mp="2skc52e5l7iau">
                    {PACKAGE_INFO_URLS[activePackageInfo] ? PACKAGE_INFO_URLS[activePackageInfo].endsWith('.pdf') ? <iframe src={PACKAGE_INFO_URLS[activePackageInfo]} className="w-full h-[500px] rounded-lg" title="Paketinformation" data-id="element-1057" data-mp="2p26b56b7vdmh" /> : <img src={PACKAGE_INFO_URLS[activePackageInfo]} alt="Paketinformation" className="w-full h-auto rounded-lg" data-id="element-1058" data-mp="2bahy90t58l4g" /> : <div className="p-10 rounded-xl border-2 border-dashed border-[#C5A55A]/30 bg-[#FFF8F0]/50 text-center" data-id="element-1059" data-mp="32zuxyjouguu6">
                        <FileText className="w-10 h-10 text-[#C5A55A]/40 mx-auto mb-3" data-id="element-1060" data-mp="2u1ix7ou9kgo0" />
                        <p className="text-sm text-[#1A1A1A]/50 font-medium" data-id="element-1061" data-mp="1nknqgxnolmr2">
                          Information kommer snart
                        </p>
                        <p className="text-xs text-[#1A1A1A]/40 mt-1" data-id="element-1062" data-mp="1ikj78zve2dx5">
                          Kontakta oss på order@topnotchbakery.se för aktuellt
                          utbud och priser
                        </p>
                      </div>}
                  </div>
                  <div className="p-4 border-t border-[#C5A55A]/10 shrink-0" data-id="element-1063" data-mp="1b1s2aqjp7zr3">
                    <Button type="button" variant="outline" onClick={() => setActivePackageInfo(null)} className="w-full" size="sm" data-id="element-1064" data-mp="1f2vije5nrxsu">
                      Stäng
                    </Button>
                  </div>
                </div>
              </div>}

            {/* === CATERING: KATEGORIER === */}
            {isCatering && <div className="space-y-6" data-id="element-1065" data-mp="id4cqirx2o7q">
                {/* Event info */}
                <div className="space-y-4" data-id="element-1066" data-mp="ncr19od73qvu">
                  <SectionHeading icon={UtensilsCrossed} title="Om ert event" data-id="element-1067" data-mp="nbpdqkeztbx8" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1068" data-mp="3qfhit4p6x75">
                    <div data-id="element-1069" data-mp="3ux8flo472g7g">
                      <label className={labelClass} data-id="element-1070" data-mp="3uy1pgxyg9ltt">Typ av event {req}</label>
                      <input type="text" name="eventType" required value={formData.eventType} onChange={handleChange} className={inputClass} placeholder="T.ex. bröllop, företagsfest, mingel, begravning" data-id="element-1071" data-mp="crdl5ab852p7" />
                    </div>
                    <div data-id="element-1072" data-mp="wfw2j5pznbtc">
                      <label className={labelClass} data-id="element-1073" data-mp="wf354vcx5ynv">Antal personer {req}</label>
                      <input type="number" name="antalPersoner" required min="1" value={formData.antalPersoner} onChange={handleChange} className={inputClass} placeholder="Ungefärligt antal" data-id="element-1074" data-mp="rhktuybfj7cp" />
                    </div>
                  </div>
                </div>

                {/* What to order */}
                <div className="space-y-4" data-id="element-1075" data-mp="34oj1evcz02w6">
                  <SectionHeading icon={ShoppingBag} title="Vad vill ni beställa?" data-id="element-1076" data-mp="38otip467qfqc" />
                  <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-1077" data-mp="2bzlwdtxrs2ld">
                    Bocka i det som är intressant. Beskriv sedan mer i detalj
                    längre ner.
                  </p>
                  <div className="space-y-2" data-id="element-1078" data-mp="2ky7938gk8ne8">
                    {CATERING_CATEGORIES.map(cat => <div key={cat.id} data-id="element-1079" data-mp="2pz4stph7xphu">
                        <label className="flex items-start gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-1080" data-mp="2pvz44bci4pmn">
                          <input type="checkbox" checked={selectedCategories.includes(cat.id)} onChange={() => toggleCategory(cat.id)} className="w-4 h-4 mt-0.5 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1081" data-mp="fbrodjgg6dqk" />
                          <div data-id="element-1082" data-mp="1ijzw0nnxyd3">
                            <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1083" data-mp="3sqo4h37zp387">
                              {cat.label}
                            </span>
                            <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-1084" data-mp="3nqjl95fp5uea">
                              {cat.desc}
                            </p>
                          </div>
                        </label>
                        {selectedCategories.includes(cat.id) && <div className="ml-7 mt-2 mb-1 animate-in slide-in-from-top-1 duration-150" data-id="element-1085" data-mp="1h03oxyqo9esq">
                            <textarea value={categoryNotes[cat.id] || ''} onChange={e => setCategoryNotes(prev => ({
                      ...prev,
                      [cat.id]: e.target.value
                    }))} className={textareaClass} style={{
                      minHeight: '60px'
                    }} placeholder={`Beskriv kort era önskemål för ${cat.label.replace(/^[^\s]+\s/, '')}...`} data-id="element-1086" data-mp="1d0bax6iiegfz" />
                          </div>}
                      </div>)}
                  </div>
                </div>

                {/* Drink */}
                <div className="space-y-3 p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-1087" data-mp="24sdkhvnjauep">
                  <label className={labelClass} data-id="element-1088" data-mp="29t1807ck0t46">
                    Vill ni ha dryck till eventet?
                  </label>
                  <div className="flex gap-3" data-id="element-1089" data-mp="21dc1s551qlqi">
                    {['Ja', 'Nej'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-1090" data-mp="1xbqgbveg4z4p">
                        <input type="radio" name="cateringDrink" value={option} checked={formData.cateringDrink === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1091" data-mp="1iiev8mqmgsfg" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-1092" data-mp="zrx7yxok6p1h">{option}</span>
                      </label>)}
                  </div>
                  {formData.cateringDrink === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-1093" data-mp="eaylgbd0s64j">
                      <label className={labelClass} data-id="element-1094" data-mp="ecrwe2uo1cic">
                        Vilken typ av dryck önskas?
                      </label>
                      <textarea name="cateringDrinkDetails" value={formData.cateringDrinkDetails} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '60px'
                }} placeholder="T.ex. kaffe, te, juice, läsk, vatten..." data-id="element-1095" data-mp="o9nz1rxvv4ez" />
                    </div>}
                </div>

                {/* Extra services */}
                <div className="space-y-4" data-id="element-1096" data-mp="1bwmb8deyzq73">
                  <SectionHeading icon={User} title="Extratjänster" data-id="element-1097" data-mp="1fzixqf7ms6gb" />
                  <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-1098" data-mp="30icy1maycguz">
                    Vi kan hjälpa till med mer än bara mat och fika.
                  </p>

                  {/* Project management */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-1099" data-mp="2rhn8h0cabmpp">
                    <div data-id="element-1100" data-mp="2mitqaum1z7j7">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1101" data-mp="2mjlnuaz7nfc4">
                        📋 Projektledning av eventet
                      </span>
                      <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-1102" data-mp="3k85ady19vi1j">
                        Vi hjälper er planera och koordinera mat, logistik och
                        upplägg för ert event.
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-1103" data-mp="35gedzyd30028">
                      {['Ja, intresserad', 'Nej tack'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-1104" data-mp="6sqs710dzk42">
                          <input type="radio" name="cateringProjectMgmt" value={option === 'Ja, intresserad' ? 'Ja' : 'Nej'} checked={formData.cateringProjectMgmt === (option === 'Ja, intresserad' ? 'Ja' : 'Nej')} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1105" data-mp="eqxtm0foan4n" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-1106" data-mp="oky3sgptaik8">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {formData.cateringProjectMgmt === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-1107" data-mp="2z2jcrb6suiz5">
                        <label className={labelClass} data-id="element-1108" data-mp="340ul1hhg4lf9">
                          Berätta kort vad ni behöver hjälp med
                        </label>
                        <textarea name="cateringProjectMgmtDetails" value={formData.cateringProjectMgmtDetails} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '60px'
                  }} placeholder="T.ex. 'Vi behöver hjälp med planering av meny, uppdukning och tidsschema för kvällen'" data-id="element-1109" data-mp="3d0q03xe4h0kk" />
                      </div>}
                  </div>

                  {/* Hire staff */}
                  <div className="p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-3" data-id="element-1110" data-mp="9l2ed7lcxrpt">
                    <div data-id="element-1111" data-mp="25nxd2rw1fmqg">
                      <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-1112" data-mp="25l2tf2kum5y3">
                        👥 Hyra in personal
                      </span>
                      <p className="text-xs text-[#1A1A1A]/50 mt-0.5" data-id="element-1113" data-mp="2fd8ju7vgcfb6">
                        Behöver ni servicepersonal, serveringspersonal eller
                        annan hjälp på plats under eventet?
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-1114" data-mp="2ofhtkviwc4ni">
                      {['Ja, intresserad', 'Nej tack'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-white transition-colors has-[:checked]:bg-white has-[:checked]:border-[#C5A55A] flex-1" data-id="element-1115" data-mp="2ybktduq9zr7a">
                          <input type="radio" name="cateringHireStaff" value={option === 'Ja, intresserad' ? 'Ja' : 'Nej'} checked={formData.cateringHireStaff === (option === 'Ja, intresserad' ? 'Ja' : 'Nej')} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1116" data-mp="yz8q5en79lwd" />
                          <span className="text-sm text-[#1A1A1A]" data-id="element-1117" data-mp="2wv71r39ysm3t">
                            {option}
                          </span>
                        </label>)}
                    </div>
                    {formData.cateringHireStaff === 'Ja' && <div className="animate-in slide-in-from-top-2 duration-200" data-id="element-1118" data-mp="g60h2qymmigh">
                        <label className={labelClass} data-id="element-1119" data-mp="g47998ca1s6w">
                          Berätta kort vad ni behöver
                        </label>
                        <textarea name="cateringHireStaffDetails" value={formData.cateringHireStaffDetails} onChange={handleChange} className={textareaClass} style={{
                    minHeight: '60px'
                  }} placeholder="T.ex. '2 serveringspersonal under 4 timmar', 'Personal som sköter uppdukning och servering'" data-id="element-1120" data-mp="2h8j7oktph3mj" />
                      </div>}
                  </div>
                </div>
              </div>}

            {/* === ALLERGIER (företag & catering) === */}
            {(isFöretag || isCatering) && <div className="space-y-4" data-id="element-1121" data-mp="1kg5w9vcmvolu">
                <SectionHeading icon={AlertTriangle} title="Allergier & specialkost" data-id="element-1122" data-mp="1fj5s5pzcgvxd" />
                <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-1123" data-mp="1akkw82r4kyeq">
                  Bocka i eventuella allergier och ange hur många personer det
                  gäller.
                </p>
                <div className="space-y-3" data-id="element-1124" data-mp="15de9az08ub3i">
                  {ALLERGIES.map(allergy => <div key={allergy} data-id="element-1125" data-mp="1aeldko36gs5f">
                      <label className={`${checkboxClass} ${selectedAllergies.includes(allergy) ? '' : ''}`} data-id="element-1126" data-mp="1fd4ziohru3xs">
                        <input type="checkbox" checked={selectedAllergies.includes(allergy)} onChange={() => toggleAllergy(allergy)} className="w-4 h-4 rounded text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-1127" data-mp="hqxz7qsi2xr8" />
                        <span className="text-sm text-[#1A1A1A] flex-1" data-id="element-1128" data-mp="3v4j62v7tvnad">
                          {allergy}
                        </span>
                        {selectedAllergies.includes(allergy) && allergy !== 'Annat' && <div className="flex items-center gap-2 ml-auto" onClick={e => e.preventDefault()} data-id="element-1129" data-mp="3s0v7t00o1e3s">
                              <input type="number" min="1" value={allergyCounts[allergy] || ''} onChange={e => {
                      e.stopPropagation();
                      setAllergyCount(allergy, e.target.value);
                    }} onClick={e => e.stopPropagation()} className="w-16 h-8 px-2 text-center rounded-md border border-[#C5A55A]/30 bg-white text-sm focus:border-[#C5A55A] focus:ring-1 focus:ring-[#C5A55A]/20 outline-none" placeholder="0" data-id="element-1130" data-mp="5mbsn13v9uw9" />
                              <span className="text-xs text-[#1A1A1A]/40 whitespace-nowrap" data-id="element-1131" data-mp="10vxkgz58v8q5">
                                pers
                              </span>
                            </div>}
                      </label>
                      {selectedAllergies.includes(allergy) && allergy === 'Annat' && <div className="mt-2 ml-9 flex items-center gap-3 animate-in slide-in-from-top-1 duration-150" data-id="element-1132" data-mp="1jmopjoi78cyq">
                            <input type="text" name="allergiAnnat" value={formData.allergiAnnat} onChange={handleChange} className={`${inputClass} flex-1`} placeholder="T.ex. sojafri, sesamfri..." data-id="element-1133" data-mp="1omb17tidpl5f" />
                            <div className="flex items-center gap-2 shrink-0" data-id="element-1134" data-mp="27cj0g0iyjrgc">
                              <input type="number" min="1" value={allergyCounts['Annat'] || ''} onChange={e => setAllergyCount('Annat', e.target.value)} className="w-16 h-8 px-2 text-center rounded-md border border-[#C5A55A]/30 bg-white text-sm focus:border-[#C5A55A] focus:ring-1 focus:ring-[#C5A55A]/20 outline-none" placeholder="0" data-id="element-1135" data-mp="1ild3l3sldsah" />
                              <span className="text-xs text-[#1A1A1A]/40" data-id="element-1136" data-mp="1rl9xpkxr3jcx">
                                pers
                              </span>
                            </div>
                          </div>}
                    </div>)}
                </div>
              </div>}

            {/* === BESTÄLLNING / MEDDELANDE === */}
            <div className="space-y-4" data-id="element-1137" data-mp="2mw6e4bk05ydc">
              <SectionHeading icon={MessageSquare} title={isMeny ? 'Meddelande' : 'Beskriv din beställning'} data-id="element-1138" data-mp="2mx837jxusle2" />
              <div data-id="element-1139" data-mp="2wtb30j58g7xu">
                <label className={labelClass} data-id="element-1140" data-mp="2wssykz8f2neu">
                  {isMeny ? 'Vad vill du veta mer om?' : isFöretag ? 'Beskriv vad ni är sugna på' : 'Beskriv vad ni önskar och ställ frågor'}{' '}
                  {req}
                </label>
                <p className="text-xs text-[#1A1A1A]/50 mb-2" data-id="element-1141" data-mp="3an2ehx2iy1w7">
                  {isMeny ? 'Ställ frågor om månadens meny, allergier, eller berätta vad du är intresserad av.' : isFöretag ? 'Berätta vad ni tänker er i varje paket, önskemål om innehåll, eller andra frågor.' : 'Beskriv vad ni tänker er, ställ frågor, eller berätta om särskilda önskemål.'}
                </p>
                <textarea name="details" required value={formData.details} onChange={handleChange} className={textareaClass} placeholder={isMeny ? 'Ditt meddelande...' : 'Beskriv din beställning här...'} data-id="element-1142" data-mp="1bpwhpnxtonkq" />
              </div>
            </div>

            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1143" data-mp="1zh1v7wgaj3rv">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1144" data-mp="1ki7y0gz82umz" />
                <p data-id="element-1145" data-mp="1pi2mkdviz84v">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-1146" data-mp="93t5m4tm4lo2">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-1147" data-mp="e4gxespo16jc">
            Avbryt
          </Button>
          <Button type="submit" form="order-form" isLoading={isSubmitting} className="min-w-[160px]" data-id="element-1148" data-mp="2ndy9tefn1wqs">
            <Send className="w-4 h-4 mr-2" data-id="element-1149" data-mp="2xbcmr42cs3ec" />
            Skicka offertförfrågan
          </Button>
        </div>
      </div>
    </div>;
}